/**
 * @file ContractDrawerTabsView.tsx
 * @description Pure presentation for the three-tab strip inside the
 *   contract drawer + mobile contract page (SE-4347 AC-6): Contract,
 *   Transfer Agreement, Live Tracking [SOON]. Live Tracking tab is
 *   suppressed when `isArchived` is true (SE-4349 — archived rows show
 *   only two tabs per the Lovable prototype). The active tab is marked
 *   with a primary-500 underline. The strip is horizontally scrollable
 *   on narrow surfaces so the Live Tracking tab remains reachable on
 *   mobile without truncation.
 * @module features/contracts/components/ContractDrawerTabs/ContractDrawerTabsView
 */

import { Activity, FileText, Repeat } from "lucide-react-native";
import React, { memo } from "react";

import { ScrollView } from "@/components/atoms/ScrollView";
import { Box } from "@/components/atoms/Box";
import { Badge, BadgeText } from "@/components/atoms/Badge";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Discriminant for the three tabs inside the contract drawer body.
 * `live_tracking` is only rendered when the drawer is NOT archived
 * (SE-4349 AC-8) — the strip reduces to two tabs for archived rows.
 */
export type ContractDrawerTabKey =
  | "contract"
  | "transfer_agreement"
  | "live_tracking";

/** Props for {@link ContractDrawerTabsView}. */
export interface ContractDrawerTabsViewProps {
  /** Whether the Contract tab is the active one. */
  readonly isContractActive: boolean;
  /** Whether the Transfer Agreement tab is the active one. */
  readonly isTransferActive: boolean;
  /** Whether the Live Tracking tab is the active one. */
  readonly isLiveActive: boolean;
  /** Whether the Live Tracking tab should be hidden (archived mode). */
  readonly isArchived: boolean;
  /** Pre-bound press handler for the Contract tab. */
  readonly onPressContract: () => void;
  /** Pre-bound press handler for the Transfer Agreement tab. */
  readonly onPressTransferAgreement: () => void;
  /** Pre-bound press handler for the Live Tracking tab. */
  readonly onPressLiveTracking: () => void;
}

/**
 * Resolves the Tailwind class for a tab's icon + label given whether it
 * is the currently active tab. Kept as a pure helper so the JSX stays
 * compact for the single-component-per-file rule.
 * @param isActive - Whether the tab is active
 * @returns The foreground class string
 */
function tabLabelClass(isActive: boolean): string {
  return isActive ? "text-content-primary" : "text-content-muted";
}

/**
 * Pure presentation for the drawer's three-tab strip.
 * @param props - View props
 * @param props.isContractActive - Whether the Contract tab is active
 * @param props.isTransferActive - Whether the Transfer Agreement tab is active
 * @param props.isLiveActive - Whether the Live Tracking tab is active
 * @param props.isArchived - Hides the Live Tracking tab when true
 * @param props.onPressContract - Handler for the Contract tab
 * @param props.onPressTransferAgreement - Handler for the Transfer Agreement tab
 * @param props.onPressLiveTracking - Handler for the Live Tracking tab
 * @returns The rendered tab strip
 */
const ContractDrawerTabsView = ({
  isContractActive,
  isTransferActive,
  isLiveActive,
  isArchived,
  onPressContract,
  onPressTransferAgreement,
  onPressLiveTracking,
}: ContractDrawerTabsViewProps) => (
  <Box testID="contract-drawer-tabs">
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1 }}
    >
      {/* Intentionally NO `w-full` on this HStack — locking to the
       * viewport width would defeat horizontal scrolling inside the
       * ScrollView on narrow surfaces and hide the Live Tracking tab
       * on mobile. SE-4784 — the inner `View testID="contract-drawer-tabs-list"`
       * wrap lets the regression spec target the tab strip's bounding
       * box without reaching for class substrings (gluestack `HStack`
       * doesn't surface `testID` directly). */}
      <Box testID="contract-drawer-tabs-list">
        <Stack
          direction="horizontal"
          className="gap-6 border-b border-outline-default px-4"
        >
          <Pressable
            testID="contract-drawer-tab-contract"
            onPress={onPressContract}
          >
            <Stack className="relative pb-3">
              <Stack direction="horizontal" className="items-center gap-2">
                <Icon
                  as={FileText}
                  size="sm"
                  className={tabLabelClass(isContractActive)}
                />
                <Text
                  variant="body-strong"
                  className={tabLabelClass(isContractActive)}
                >
                  Contract
                </Text>
              </Stack>
              {isContractActive ? (
                <Box className="absolute inset-x-0 -bottom-px h-micro bg-needs-semantic-primary-500" />
              ) : null}
            </Stack>
          </Pressable>

          <Pressable
            testID="contract-drawer-tab-transfer_agreement"
            onPress={onPressTransferAgreement}
          >
            <Stack className="relative pb-3">
              <Stack direction="horizontal" className="items-center gap-2">
                <Icon
                  as={Repeat}
                  size="sm"
                  className={tabLabelClass(isTransferActive)}
                />
                <Text
                  variant="body-strong"
                  className={tabLabelClass(isTransferActive)}
                >
                  Transfer Agreement
                </Text>
              </Stack>
              {isTransferActive ? (
                <Box className="absolute inset-x-0 -bottom-px h-micro bg-needs-semantic-primary-500" />
              ) : null}
            </Stack>
          </Pressable>

          {isArchived ? null : (
            <Pressable
              testID="contract-drawer-tab-live_tracking"
              onPress={onPressLiveTracking}
            >
              <Stack className="relative pb-3">
                <Stack direction="horizontal" className="items-center gap-2">
                  <Icon
                    as={Activity}
                    size="sm"
                    className={tabLabelClass(isLiveActive)}
                  />
                  <Text
                    variant="body-strong"
                    className={tabLabelClass(isLiveActive)}
                  >
                    Live Tracking
                  </Text>
                  <Box testID="contract-drawer-tab-live_tracking-soon-badge">
                    <Badge size="sm" action="info" variant="solid">
                      <BadgeText>SOON</BadgeText>
                    </Badge>
                  </Box>
                </Stack>
                {isLiveActive ? (
                  <Box className="absolute inset-x-0 -bottom-px h-micro bg-needs-semantic-primary-500" />
                ) : null}
              </Stack>
            </Pressable>
          )}
        </Stack>
      </Box>
    </ScrollView>
  </Box>
);

ContractDrawerTabsView.displayName = "ContractDrawerTabsView";

export default memo(ContractDrawerTabsView);
